import { CartItem, StoreSeller, PaymentMethod, PaymentMethodLabel, StoreProductDetail, StoreVariantColor } from '../models';
import { formatCurrency } from './currency';
import { WHATSAPP_NUMBER } from '../constants/contact';
import { CheckoutTotals } from './pricing';

interface WhatsAppPaymentEntry {
  paymentMethod: PaymentMethod;
  amountPaid: number;
  installments?: number;
}

interface OrderWhatsAppParams {
  seller: StoreSeller;
  items: CartItem[];
  totals: CheckoutTotals;
  orderId?: number;
  paymentEntries?: WhatsAppPaymentEntry[];
  customerName?: string;
  customerPhone?: string;
  observation?: string;
  installmentsInterestFree?: boolean;
}

function buildSellerPhone(whatsAppPhone: string): string {
  const digits = whatsAppPhone.replace(/\D/g, '');
  return digits.startsWith('55') ? digits : `55${digits}`;
}

function describePayment(entry: WhatsAppPaymentEntry, interestFree: boolean): string {
  let label = PaymentMethodLabel[entry.paymentMethod];
  const installments = entry.installments ?? 1;
  if (entry.paymentMethod === PaymentMethod.CreditCard && installments > 1) {
    const suffix = interestFree ? ' sem juros' : '';
    label += ` — ${installments}x de R$ ${formatCurrency(entry.amountPaid / installments)}${suffix}`;
  }
  return `• ${label}: R$ ${formatCurrency(entry.amountPaid)}`;
}

export function buildWhatsAppUrl({
  seller,
  items,
  totals,
  orderId,
  paymentEntries,
  customerName,
  customerPhone,
  observation,
  installmentsInterestFree = true,
}: OrderWhatsAppParams): string {
  const lines = items.map((i) => {
    const sku = i.variantSku ? ` [${i.variantSku}]` : '';
    const giftTag = i.isGift ? ' (presente)' : '';
    return `• ${i.productName}${sku} (${i.variantLabel}) x${i.quantity} — R$ ${formatCurrency((i.promotionalPrice ?? i.price) * i.quantity)}${giftTag}`;
  });

  const messageParts: string[] = [];

  if (customerName && customerPhone) {
    messageParts.push(`*Cliente:* ${customerName}`, `*WhatsApp:* ${customerPhone}`, '');
  }

  messageParts.push('Olá! Gostaria de fazer um pedido:', '');

  if (orderId && orderId > 0) {
    messageParts.push(`*Pedido #${orderId}*`, '');
  }

  messageParts.push(...lines, '');

  if (totals.pixDiscountApplies) {
    messageParts.push(
      `Produtos: R$ ${formatCurrency(totals.gross)}`,
      `Desconto Pix: −R$ ${formatCurrency(totals.pixDiscount)}`,
      `*Total dos produtos: R$ ${formatCurrency(totals.net)}*`
    );
  } else {
    messageParts.push(`*Total dos produtos: R$ ${formatCurrency(totals.net)}*`);
  }
  messageParts.push('Frete a combinar.');

  if (paymentEntries && paymentEntries.length > 0) {
    messageParts.push(
      '',
      '*Formas de pagamento:*',
      ...paymentEntries.map((e) => describePayment(e, installmentsInterestFree))
    );
  }

  const giftItems = items.filter((i) => i.isGift);
  if (giftItems.length > 0) {
    messageParts.push('', `*Presente:* ${giftItems.length} de ${items.length} ${items.length === 1 ? 'item' : 'itens'} ${giftItems.length === 1 ? 'é' : 'são'} presente`);
  }

  if (observation) {
    messageParts.push('', `*Observação:* ${observation}`);
  }

  return `https://wa.me/${buildSellerPhone(seller.whatsAppPhone)}?text=${encodeURIComponent(messageParts.join('\n'))}`;
}

export function buildStoreContactWhatsAppUrl(message: string): string {
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
}

export function buildOrderFollowUpWhatsAppUrl(orderId: number): string {
  return buildStoreContactWhatsAppUrl(`Olá! Acabei de fazer o pedido nº ${orderId} no site e gostaria de combinar o pagamento e o frete.`);
}

export function buildProductInquiryWhatsAppUrl(
  product: StoreProductDetail,
  displayPrice: number | null,
  selectedColor?: StoreVariantColor | null,
  selectedVariantLabel?: string | null
): string {
  const colorPart = selectedColor ? ` — Cor: ${selectedColor.name}` : '';
  const variantPart = selectedVariantLabel ? ` — ${selectedVariantLabel}` : '';
  const intro =
    displayPrice !== null
      ? `Olá! Tenho interesse neste produto: *${product.name}* (${product.sku})${colorPart}${variantPart}. `
      : `Olá! Gostaria de saber o preço deste produto: *${product.name}* (${product.sku})${colorPart}${variantPart}. `;
  const pricePart = displayPrice !== null ? `Preço: R$ ${formatCurrency(displayPrice)}. ` : '';
  const message =
    `${intro}${pricePart}Link: ${window.location.href}. Poderia me passar mais detalhes?`;

  return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
}

export function buildCartInquiryWhatsAppUrl(): string {
  const message = 'Olá! Tenho uma dúvida sobre a minha sacola de compras.';
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
}
